import { OrderSetupStatusEnum } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useOrderSetupListQuery } from "Settings/Modules/OrderSetup/Api/Query/useOrderSetupListQuery";
import { OrderSetupListRow } from "Settings/Modules/OrderSetup/Components/OrderSetupListRow";
import { Alert } from "Shared/Components/Alert";
import { ChipFilterTab } from "Shared/Components/ChipFilterTabs";
import { Loading } from "Shared/Components/Loading/Loading";
import { CloudDone, CloudOff } from "@mui/icons-material";
import { Box, Divider, Stack, Typography } from "@mui/material";
import { isEmpty } from "lodash";
import { type FC, useMemo, useState } from "react";

type Props = {
	marketId: number;
	verticalId: number;
	orderSetupId?: number;
};

const orderSetupResource = Resources.base.orderSetup.shared;

export const OrderSetupList: FC<Props> = ({
	marketId,
	verticalId,
	orderSetupId,
}) => {
	const { t } = useResource();
	const [filterStatus, setFilterStatus] = useState<
		OrderSetupStatusEnum | undefined
	>(undefined);
	const { data: orderSetupList, isLoading } = useOrderSetupListQuery(
		verticalId,
		marketId,
	);

	const deployedSetup = useMemo(
		() =>
			orderSetupList?.find((os) => os.status === OrderSetupStatusEnum.deployed),
		[orderSetupList],
	);

	const allSetups = useMemo(
		() =>
			orderSetupList?.filter(
				(os) => os.status !== OrderSetupStatusEnum.deployed,
			) ?? [],
		[orderSetupList, filterStatus],
	);

	const filterVisibleSetups = (status: OrderSetupStatusEnum) =>
		filterStatus === undefined || status === filterStatus;

	const allSetupsFiltered = useMemo(
		() =>
			allSetups
				.filter((s) => filterVisibleSetups(s.status))
				.sort((a, _) => (a.status === OrderSetupStatusEnum.ready ? -1 : 1)),
		[allSetups, filterStatus],
	);

	const handleFilterStatus = (status: OrderSetupStatusEnum) => {
		if (filterStatus === status) {
			setFilterStatus(undefined);
			return;
		}

		setFilterStatus(status);
	};

	return (
		<Stack rowGap={2} height="100%" flexGrow={0} overflow="auto">
			<Stack flexShrink={0}>
				<Typography variant="h6" mb={1}>
					{t(orderSetupResource.deployedSetup)}
				</Typography>
				{deployedSetup && (
					<Stack rowGap={1}>
						<Alert
							message={t(orderSetupResource.deployed)}
							color="success"
							icon={<CloudDone />}
							sx={{ marginTop: 1 }}
						/>
						<Box overflow="hidden">
							<OrderSetupListRow
								key={deployedSetup.id}
								orderSetup={deployedSetup}
								disabled={!marketId}
								selected={deployedSetup.id === orderSetupId}
							/>
						</Box>
					</Stack>
				)}
				{!deployedSetup && (
					<Alert
						message={t(orderSetupResource.noDeployment)}
						color="error"
						icon={<CloudOff />}
						sx={{ marginTop: 1 }}
					/>
				)}
			</Stack>
			<Divider />
			<Stack overflow="auto" height="100%" rowGap={1}>
				<Typography variant="h6">{t(orderSetupResource.orderSetup)}</Typography>
				{!isEmpty(allSetups) && (
					<Stack direction="row" columnGap={1} mt={1}>
						<ChipFilterTab<OrderSetupStatusEnum>
							value={OrderSetupStatusEnum.ready}
							onClick={handleFilterStatus}
							active={filterStatus === OrderSetupStatusEnum.ready}
							label={t(orderSetupResource.statuses[OrderSetupStatusEnum.ready])}
							color={"primary"}
						/>
						<ChipFilterTab<OrderSetupStatusEnum>
							value={OrderSetupStatusEnum.draft}
							onClick={handleFilterStatus}
							active={filterStatus === OrderSetupStatusEnum.draft}
							label={t(orderSetupResource.statuses[OrderSetupStatusEnum.draft])}
							color={"primary"}
						/>
					</Stack>
				)}
				<Box overflow="auto">
					{!isEmpty(allSetupsFiltered) ? (
						allSetupsFiltered.map((orderSetup) => (
							<OrderSetupListRow
								key={orderSetup.id}
								orderSetup={orderSetup}
								disabled={!marketId}
								selected={orderSetup.id === orderSetupId}
							/>
						))
					) : isLoading ? (
						<Loading />
					) : (
						<Alert
							message={t(orderSetupResource.noResults)}
							color="grey"
							sx={{ marginTop: 1 }}
						/>
					)}
				</Box>
			</Stack>
		</Stack>
	);
};
